import { useEffect, useRef } from 'react';

/** Czy zdarzenie pochodzi z pola, w którym użytkownik właśnie coś pisze. */
const isTypingTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

/**
 * Globalny skrót klawiaturowy.
 *
 * @param {string} combo - np. 'mod+k', 'escape', 'shift+?'.
 *   'mod' to Ctrl na Windowsie/Linuksie i Cmd na macOS.
 * @param {Function} handler
 * @param {object} options
 * @param {boolean} options.enabled - pozwala wyłączyć skrót warunkowo.
 * @param {boolean} options.allowInInputs - domyślnie skróty nie działają podczas
 *   pisania w polu formularza, żeby nie przechwytywać znaków. Wyjątkiem są
 *   skróty z modyfikatorem oraz Escape, które mają działać wszędzie.
 */
export function useHotkey(combo, handler, { enabled = true, allowInInputs = false } = {}) {
  // Handler w ref, żeby zmiana funkcji (a przy każdym renderze jest nowa)
  // nie powodowała odpinania i podpinania listenera na nowo.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return undefined;

    const parts = combo.toLowerCase().split('+');
    const key = parts[parts.length - 1];
    const needsMod = parts.includes('mod');
    const needsShift = parts.includes('shift');
    const needsAlt = parts.includes('alt');

    const onKeyDown = (event) => {
      const pressedMod = event.ctrlKey || event.metaKey;
      if (needsMod !== pressedMod) return;
      if (needsShift !== event.shiftKey) return;
      if (needsAlt !== event.altKey) return;
      if (event.key.toLowerCase() !== key) return;

      const skipInputGuard = allowInInputs || needsMod || key === 'escape';
      if (!skipInputGuard && isTypingTarget(event.target)) return;

      event.preventDefault();
      handlerRef.current(event);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [combo, enabled, allowInInputs]);
}

export default useHotkey;
